import { useEffect, useState } from 'react'
import {
  disconnectXero, fetchNominals, fetchXeroCalls, fetchXeroStatus, saveNominal, saveXeroSettings, searchXeroContacts, xeroConnectUrl,
  type NominalRow, type XeroCalls, type XeroContact, type XeroStatus,
} from '../lib/api.js'

/**
 * Admin → Xero (decision 213) — the portal's Xero connection and the three
 * settings a purchase order needs: the supplier contact (picked from a live
 * search of Xero contacts, never typed), the nominal account code and the
 * tax type. Connecting is a one-time OAuth round trip by a Wave9 admin; the
 * weekly keep-alive job (Scheduled jobs) rotates the token thereafter.
 *
 * The OAuth callback lands the browser back on `/?xero=connected|error` —
 * App.tsx opens this page for it and the banner below reads the outcome.
 */

const TAX_TYPES: Array<{ code: string; label: string }> = [
  { code: 'INPUT2', label: 'INPUT2 — 20% (VAT on expenses)' },
  { code: 'INPUT', label: 'INPUT — 5% (reduced rate on expenses)' },
  { code: 'ZERORATEDINPUT', label: 'ZERORATEDINPUT — zero rated' },
  { code: 'EXEMPTINPUT', label: 'EXEMPTINPUT — exempt' },
  { code: 'NONE', label: 'NONE — no VAT' },
]

export function XeroSettings({ outcome }: { outcome: { kind: 'connected' | 'error'; reason: string } | null }) {
  const [status, setStatus] = useState<XeroStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(outcome?.kind === 'connected' ? `Connected to ${outcome.reason || 'Xero'}.` : null)
  const [busy, setBusy] = useState(false)
  const [term, setTerm] = useState('')
  const [hits, setHits] = useState<XeroContact[] | null>(null)
  const [accountCode, setAccountCode] = useState('')
  const [taxType, setTaxType] = useState('')
  // Decision 214: sales nominal → purchase nominal, one row per code in use.
  const [nominals, setNominals] = useState<NominalRow[] | null>(null)
  const [draft, setDraft] = useState<Record<string, { purchase: string; note: string }>>({})

  const load = () => fetchXeroStatus().then((s) => {
    setStatus(s); setAccountCode(s.settings.accountCode); setTaxType(s.settings.taxType)
  }).catch((e: Error) => setError(e.message))
  useEffect(() => { void load() }, [])
  // Decision 233: the call ledger by hour — who spent the day's 5,000.
  const [calls, setCalls] = useState<XeroCalls | null>(null)
  useEffect(() => { fetchXeroCalls(24).then(setCalls).catch(() => setCalls(null)) }, [])
  useEffect(() => {
    fetchNominals().then((r) => {
      setNominals(r.nominals)
      setDraft(Object.fromEntries(r.nominals.map((n) => [n.salesCode, { purchase: n.purchaseCode ?? '', note: n.note ?? '' }])))
    }).catch((e: Error) => setError(e.message))
  }, [])
  const saveRow = async (salesCode: string) => {
    const d = draft[salesCode]; if (!d) return
    setBusy(true); setError(null)
    try {
      const r = await saveNominal(salesCode, d.purchase.trim(), d.note.trim() || null)
      setNominals(r.nominals)
      setNotice(d.purchase.trim() ? `Sales ${salesCode} → purchase ${d.purchase.trim()} saved.` : `Mapping for ${salesCode} removed.`)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  useEffect(() => { if (outcome?.kind === 'error') setError(outcome.reason || 'Connecting to Xero failed.') }, [outcome])

  const search = async () => {
    setBusy(true); setError(null)
    try { setHits((await searchXeroContacts(term)).contacts) } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  const pick = async (c: XeroContact) => {
    setBusy(true); setError(null)
    try {
      const r = await saveXeroSettings({ contactId: c.contactId, contactName: c.name })
      setStatus((s) => (s ? { ...s, settings: r.settings } : s)); setHits(null); setTerm('')
      setNotice(`Supplier contact set to ${c.name}.`)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  const saveCodes = async () => {
    setBusy(true); setError(null)
    try {
      const r = await saveXeroSettings({ accountCode: accountCode.trim(), taxType: taxType.trim() })
      setStatus((s) => (s ? { ...s, settings: r.settings } : s)); setNotice('Saved.')
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  const disconnect = async () => {
    if (!window.confirm('Disconnect Xero? Purchase orders cannot be raised until an admin reconnects.')) return
    setBusy(true); setError(null)
    try { const r = await disconnectXero(); setStatus(r.status); setNotice('Disconnected.') } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  if (error && !status) return <p className="pill pill--stop">{error}</p>
  if (!status) return <p className="muted">Loading…</p>
  const s = status.settings
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Xero</h1>
          <p>
            The portal's connection to Xero, and what a purchase order carries. A PO is raised from
            Supplier quotes (or the builder's distributor chip) as a <strong>draft</strong> — GBP, amounts
            ex VAT per line, the quote's current quantities and costs — to the supplier contact below.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--stop" data-testid="xero-error"><span>{error}</span><button className="btn btn--ghost btn--sm" onClick={() => setError(null)}>Dismiss</button></div>}
      {notice && <div className="banner" data-testid="xero-notice"><span>{notice}</span><button className="btn btn--ghost btn--sm" onClick={() => setNotice(null)}>Dismiss</button></div>}

      <div className="card" data-testid="xero-connection">
        <div className="card__head"><h2>Connection</h2>
          {status.connected
            ? <span className="pill pill--ok">connected</span>
            : status.configured ? <span className="pill pill--warn">not connected</span> : <span className="pill pill--stop">not configured</span>}
        </div>
        <div className="card__body">
          {!status.configured && (
            <p>
              Xero is not configured on this deploy — set {status.missingVars.map((v, i) => <span key={v}>{i > 0 ? ' and ' : ''}<code>{v}</code></span>)} in the
              Netlify environment variables and redeploy (env vars only take effect on a new deploy).
            </p>
          )}
          {status.configured && !status.connected && (
            <p>
              Nobody has connected Xero yet. Connecting sends you to Xero to sign in and approve the
              portal for the Wave 9 organisation; you come straight back here.
            </p>
          )}
          {status.connected && (
            <dl className="kv">
              <dt>Organisation</dt><dd><strong>{status.tenantName}</strong></dd>
              <dt>Connected</dt><dd>{when(status.connectedAt)} by {status.connectedBy}</dd>
              <dt>Token last rotated</dt><dd>{when(status.refreshedAt)}</dd>
              <dt>Last keep-alive</dt><dd>{when(status.lastKeepaliveAt)} <span className="muted small">(weekly — Scheduled jobs → Xero token keep-alive)</span></dd>
              <dt>API calls left today</dt><dd>
                {status.budget.dayRemaining == null ? <span className="muted">not read yet — Xero reports it with every call</span>
                  : <>{status.budget.low ? <span className="pill pill--warn">{status.budget.dayRemaining} of {status.budget.dayLimit.toLocaleString('en-GB')}</span> : <strong>{status.budget.dayRemaining} of {status.budget.dayLimit.toLocaleString('en-GB')}</strong>}
                    <span className="muted small"> as of {when(status.budget.at)}{status.budget.blockedUntil ? ` — Xero refuses calls until ${when(status.budget.blockedUntil)}` : ''}. Below {status.budget.floor} the adoption / history runs and the hourly status sweep stand aside; invoices and purchase orders keep the rest.</span></>}
              </dd>
              {status.lastError && <><dt>Last error</dt><dd className="pill pill--stop" title={status.lastErrorAt ?? ''}>{status.lastError}</dd></>}
              {status.scopesMissing.length > 0 && <><dt>Permissions</dt><dd><span className="pill pill--warn">reconnect needed</span> <span className="small">The portal now needs <code>{status.scopesMissing.join(' ')}</code> — this connection was approved before that. Press <strong>Reconnect</strong> and approve the new list in Xero.</span></dd></>}
            </dl>
          )}
          <div className="toolbar" style={{ marginTop: 12 }}>
            {status.configured && (
              <a className={`btn ${status.connected ? '' : 'btn--primary'}`} href={xeroConnectUrl()} data-testid="xero-connect">
                {status.connected ? 'Reconnect Xero…' : 'Connect Xero…'}
              </a>
            )}
            {status.connected && <button className="btn btn--ghost" disabled={busy} onClick={() => void disconnect()}>Disconnect</button>}
          </div>
        </div>
      </div>

      {status.connected && calls && (
        <div className="card" data-testid="xero-calls">
          <div className="card__head"><h2>API calls — last 24 hours</h2>
            <span className="muted small" data-testid="xero-calls-summary">
              {calls.spent == null ? <>{calls.calls} made by the portal · Xero&apos;s count not read yet</>
                : <>{calls.spent.toLocaleString('en-GB')} used of the app&apos;s {calls.dayLimit.toLocaleString('en-GB')} a day · <strong>{calls.calls.toLocaleString('en-GB')} by the portal in the last 24 h</strong>
                  {calls.notOurs != null && calls.notOurs > 0
                    ? <> · {calls.notOurs.toLocaleString('en-GB')} unexplained — check the plan limit first</>
                    : <> · all accounted for</>}</>}
            </span>
          </div>
          <div className="card__body">
            <p className="muted small" style={{ marginTop: 0 }}>
              <strong>This is the portal&apos;s own daily allowance</strong> — Xero grants each app a limit per day (the app&apos;s Xero plan sets it:
              Starter 1,000, Core 5,000 — on Core since 19 September) and resets it once a day, at about 22:00 UK time. Xero reports what is
              <em>left</em> but never the limit itself, so the {calls.dayLimit.toLocaleString('en-GB')} here is the plan&apos;s figure, not a reading.
              Used = that limit less what Xero says is left ({calls.dayLimit.toLocaleString('en-GB')} − {calls.dayRemaining ?? '—'}{calls.readingAt ? `, read ${when(calls.readingAt)}` : ''});
              the ledger says how many of those were the portal&apos;s. <strong>If &ldquo;unexplained&rdquo; runs to thousands while the portal made a few hundred,
              the limit shown is wrong before anything else is</strong> — check the app&apos;s plan in the Xero developer portal (that is exactly how the Starter-plan
              cap of 1,000 was found). The portal&apos;s own traffic is there too, call by call (My Apps → Logs).
              <br />
              <strong>In the minutes the portal itself called</strong> — where Xero&apos;s 60-a-minute counter can be read directly — {calls.minute.calls > 0
                ? <>it saw <strong>{calls.minute.calls.toLocaleString('en-GB')}</strong> call(s) it did not make, in {calls.minute.minutesCrowded} of {calls.minute.minutesSampled} minutes{calls.minute.worst ? <> (busiest {when(calls.minute.worst.at)}: {calls.minute.worst.foreign})</> : null} — the only hard evidence of another caller on these credentials.</>
                : <>nothing else was calling ({calls.minute.minutesSampled} minutes sampled).</>}
              <br />
              Below {status.budget.floor} left, the portal&apos;s bulk jobs and the hourly invoice-status sweep stand aside so that invoices and purchase orders keep working.
              The <em>fall</em> column is a second measure that only catches a burst while the portal is idle; the daily reset shows up as a large <em>rise</em>, which is normal.
            </p>
            {calls.hours.length === 0 ? <p className="muted">No calls logged yet.</p> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Hour (UTC)</th><th className="num">Portal calls</th><th className="num">Refused (429)</th><th className="num">Left, lowest</th><th className="num">Left, last</th><th className="num" title="Calls the portal did not make, caught inside the minutes it did call">Not ours, in our minutes</th><th className="num" title="The old day-delta measure — a burst while the portal is idle">Fall beyond ours</th><th>What</th></tr></thead>
                <tbody>
                  {[...calls.hours].reverse().map((h) => (
                    <tr key={h.hour}>
                      <td>{h.hour.slice(5, 16).replace('T', ' ')}</td>
                      <td className="num">{h.calls}</td>
                      <td className={`num ${h.refused ? '' : 'muted'}`}>{h.refused || '—'}</td>
                      <td className="num">{h.lowest ?? '—'}</td>
                      <td className="num">{h.last ?? '—'}</td>
                      <td className={`num ${h.minuteForeign ? '' : 'muted'}`} title={h.minutesCrowded ? `${h.minutesCrowded} minute(s) with another caller` : undefined}>{h.minuteForeign ? <strong>{h.minuteForeign}</strong> : '—'}</td>
                      <td className={`num ${h.foreign ? '' : 'muted'}`}>{h.foreign ? h.foreign : '—'}</td>
                      <td className="muted small">{h.paths}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </div>
      )}

      <div className="card" data-testid="xero-supplier">
        <div className="card__head"><h2>Supplier contact</h2>
          {s.contactId ? <span className="pill pill--ok">set</span> : <span className="pill pill--stop">required</span>}
        </div>
        <div className="card__body">
          <p>
            Current: {s.contactName ? <strong>{s.contactName}</strong> : <span className="muted">none — purchase orders refuse until one is chosen</span>}
            {s.contactId && <span className="muted small"> · {s.contactId}</span>}
          </p>
          <p className="muted small">
            Xero holds several Ingram contacts; the hardware bills land on the <em>PROJECTS ONLY</em> one.
            Search and pick — the id is stored, never typed.
          </p>
          <div className="toolbar">
            <input className="input" style={{ width: 320 }} placeholder="Search Xero contacts (e.g. Ingram)…" value={term}
              onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search() }} disabled={!status.connected} />
            <button className="btn" disabled={!status.connected || busy || term.trim().length < 2} onClick={() => void search()}>Search</button>
          </div>
          {hits && (
            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table data-testid="xero-contact-hits">
                <thead><tr><th>Name</th><th>Email</th><th>Supplier?</th><th></th></tr></thead>
                <tbody>
                  {hits.length === 0 && <tr><td colSpan={4} className="muted">No contacts match.</td></tr>}
                  {hits.map((c) => (
                    <tr key={c.contactId}>
                      <td>{c.name}{c.contactId === s.contactId && <> <span className="pill pill--ok">current</span></>}</td>
                      <td className="small">{c.email ?? '—'}</td>
                      <td>{c.isSupplier ? 'yes' : <span className="muted">no</span>}</td>
                      <td><button className="btn btn--sm btn--primary" disabled={busy} onClick={() => void pick(c)}>Use this contact</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" data-testid="xero-nominals">
        <div className="card__head"><h2>Nominal codes — sales → purchase</h2>
          {nominals && (nominals.some((n) => n.productCount > 0 && !n.purchaseCode)
            ? <span className="pill pill--warn">{nominals.filter((n) => n.productCount > 0 && !n.purchaseCode).length} unmapped</span>
            : <span className="pill pill--ok">all mapped</span>)}
        </div>
        <div className="card__body">
          <p className="muted small">
            Every product carries a <b>sales</b> nominal in HubSpot (<code>nomcode</code>). A purchase order books each
            line to the matching <b>purchase</b> nominal — e.g. 201 (hardware &amp; one-off) → 311. The rows below are the
            sales nominals in use in the catalogue; a PO line whose sales nominal is unmapped is refused by name.
            Blank the purchase code and Save to remove a mapping.
          </p>
          {!nominals && <p className="muted">Loading…</p>}
          {nominals && nominals.length === 0 && <p className="muted small">No sales nominals in the product cache yet — run the product sync (Scheduled jobs) after the nominal column was added.</p>}
          {nominals && nominals.length > 0 && (
            <div className="table-wrap">
              <table data-testid="xero-nominal-rows">
                <thead><tr><th>Sales nominal</th><th>In use by</th><th>Purchase nominal</th><th>Note</th><th></th></tr></thead>
                <tbody>
                  {nominals.map((n) => {
                    const d = draft[n.salesCode] ?? { purchase: n.purchaseCode ?? '', note: n.note ?? '' }
                    const dirty = d.purchase !== (n.purchaseCode ?? '') || d.note !== (n.note ?? '')
                    return (
                      <tr key={n.salesCode} data-testid={`nominal-${n.salesCode}`}>
                        <td><strong>{n.salesCode}</strong>{n.productCount === 0 && <> <span className="pill pill--info" title="Mapped, but no product in the cache carries it">not in catalogue</span></>}</td>
                        <td className="small">{n.productCount > 0 ? <>{n.productCount} product{n.productCount === 1 ? '' : 's'}{n.activeCount !== n.productCount ? ` (${n.activeCount} active)` : ''}<div className="muted">e.g. {n.example}</div></> : <span className="muted">—</span>}</td>
                        <td>
                          <input className="input" style={{ width: 90 }} value={d.purchase} placeholder="e.g. 311" aria-label={`Purchase nominal for ${n.salesCode}`}
                            onChange={(e) => setDraft({ ...draft, [n.salesCode]: { ...d, purchase: e.target.value } })} />
                          {!n.purchaseCode && n.productCount > 0 && <> <span className="pill pill--warn">unmapped</span></>}
                        </td>
                        <td><input className="input" style={{ width: 220 }} value={d.note} placeholder="optional" aria-label={`Note for ${n.salesCode}`}
                          onChange={(e) => setDraft({ ...draft, [n.salesCode]: { ...d, note: e.target.value } })} /></td>
                        <td><button className={`btn btn--sm ${dirty ? 'btn--primary' : ''}`} disabled={busy || !dirty} onClick={() => void saveRow(n.salesCode)}>Save</button>
                          {n.updatedBy && <div className="muted small">{n.updatedBy}</div>}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" data-testid="xero-codes">
        <div className="card__head"><h2>Purchase order lines</h2></div>
        <div className="card__body">
          <div className="split">
            <div className="field">
              <label>Default purchase nominal</label>
              <input className="input" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} placeholder="311" />
              <div className="hint">Only for a line whose product has <b>no</b> sales nominal (ad-hoc lines, blank in HubSpot). Every other line uses the map above.</div>
            </div>
            <div className="field">
              <label>Tax type</label>
              <select className="input" value={TAX_TYPES.some((t) => t.code === taxType) ? taxType : ''} onChange={(e) => setTaxType(e.target.value)}>
                <option value="">— choose —</option>
                {TAX_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
              </select>
              <div className="hint">Line amounts are entered ex VAT; Xero adds the VAT this type implies.</div>
            </div>
          </div>
          <div className="toolbar" style={{ marginTop: 10 }}>
            <button className="btn btn--primary" disabled={busy || !accountCode.trim() || !taxType} onClick={() => void saveCodes()}>Save</button>
          </div>
        </div>
      </div>
    </>
  )
}
